import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { OPTION_SOURCES, SIGNATURE_ACTORS, SIGNATURE_TYPES_QUERY, REQUIRED_DOC_TYPES_QUERY } from '../../queries';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { SAVE_TEMPLATE_SIGNATURES } from 'features/documentTemplates/edit/mutations';
import {
  DOCUMENT_TEMPLATE_SIGNATURES_UPDATED,
  SIGNATURE_BOOKMARKS_UPDATED,
  DOCUMENT_TEMPLATE_X_OPTION_BOOKMARKS_UPDATED
} from 'features/documentTemplates/edit/subscriptions';
import { Card, FakeText } from '@totalsoft/rocket-ui';
import { SAVE_SIGNATURE_BOOKMARKS, SAVE_DOCUMENT_TEMPLATE_X_OPTION_BOOKMARKS } from '../../mutations';
import {
  signatureBookmarksValidator,
  documentTemplateXSignaturesValidator,
  documentTemplateXoptionBookmarkValidator
} from 'features/documentTemplates/validators';
import { useValidation, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import DocumentTemplateSignatureCard from './DocumentTemplateSignatureCard';
import { initialState, reducer } from '../../../reducers/signature/containerReducer';
import { useToast } from 'hooks/toasts/useToast';

const DocumentTemplateSignatureContainer = ({ data }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [
    dirtyInfoDocumentTemplateSignatureBookmark,
    setDocumentTemplateSignatureBookmarkDirtyInfo,
    resetDocumentTemplateSignatureBookmarkDirtyInfo
  ] = useDirtyInfo();
  const [signatureBookmarkValidation, validateSignatureBookmark, resetDirtyInfoDocumentTemplateSignatureBookmark] = useDirtyFieldValidation(
    signatureBookmarksValidator,
    dirtyInfoDocumentTemplateSignatureBookmark
  );
  const [
    dirtyInfoDocumentTemplateXoptionsBookmark,
    setDocumentTemplateXoptionsBookmarkDirtyInfo,
    resetDocumentTemplateXoptionsBookmarkDirtyInfo
  ] = useDirtyInfo();

  const [
    documentTemplateXoptionsBookmarkValidation,
    validateDocumentTemplateXoptionsBookmark,
    resetDirtyInfoDocumentTemplateXoptionsBookmark
  ] = useDirtyFieldValidation(documentTemplateXoptionBookmarkValidator, dirtyInfoDocumentTemplateXoptionsBookmark);

  const [documentTemplateXSignaturesValidation] = useValidation(documentTemplateXSignaturesValidator);
  const [saveBookmarksDisabled, setSaveBookmarksDisabled] = useState(false);
  const [saveDocumentTemplateXoptionsBookmarkDisabled, setSaveDocumentTemplateXOptionsBookmarkDisabled] = useState(false);

  const { data: requiredDocTypesData, loading: loadingRequiredDocTypes } = useQuery(REQUIRED_DOC_TYPES_QUERY);

  const { data: signatureTypesData, loading: loadingSignatureTypes } = useQuery(SIGNATURE_TYPES_QUERY);

  const { data: signatureActorsData, loading: loadingSignatureActors } = useQuery(SIGNATURE_ACTORS);

  const { data: optionSourcesData, loading: loadingOptionSources } = useQuery(OPTION_SOURCES);

  const [saveDocumentTemplateXSignatures] = useMutation(SAVE_TEMPLATE_SIGNATURES);

  const [saveSignatureBookmarks] = useMutation(SAVE_SIGNATURE_BOOKMARKS, {
    onError: () => {
      setSaveBookmarksDisabled(false);
    }
  });

  const [saveDocumentTemplateXoptionBookmarks] = useMutation(SAVE_DOCUMENT_TEMPLATE_X_OPTION_BOOKMARKS, {
    onError: () => {
      setSaveDocumentTemplateXOptionsBookmarkDisabled(false);
    }
  });

  useEffect(() => {
    if (data) {
      dispatch({ type: 'OnInitState', docTemplate: data });
    }
  }, [data]);

  /* Validation */

  // signatureBookmarks
  useEffect(() => {
    validateSignatureBookmark(localState.documentTemplate.signatureBookmarks, dirtyInfoDocumentTemplateSignatureBookmark);
  }, [localState.documentTemplate.signatureBookmarks, validateSignatureBookmark, dirtyInfoDocumentTemplateSignatureBookmark]);

  useEffect(() => {
    validateDocumentTemplateXoptionsBookmark(
      localState.documentTemplate.documentTemplateXOptionBookmarks,
      dirtyInfoDocumentTemplateXoptionsBookmark
    );
  }, [
    localState.documentTemplate.documentTemplateXOptionBookmarks,
    validateDocumentTemplateXoptionsBookmark,
    dirtyInfoDocumentTemplateXoptionsBookmark
  ]);

  useEffect(() => {
    setSaveBookmarksDisabled(!(signatureBookmarkValidation |> isValid));
  }, [signatureBookmarkValidation]);

  useEffect(() => {
    setSaveDocumentTemplateXOptionsBookmarkDisabled(!(documentTemplateXoptionsBookmarkValidation |> isValid));
  }, [documentTemplateXoptionsBookmarkValidation]);

  /* End Validation */

  const handleSelectedClick = (event, signatureId) => {
    if (event) {
      const newSignatures = [
        ...localState.documentTemplate.documentTemplateXSignatures,
        {
          __typename: 'DocumentTemplateXSignature',
          documentTemplateId: localState.documentTemplate.documentTemplateId,
          signatureId: signatureId,
          implicit: false
        }
      ];
      dispatch({ type: 'OnDocumentTemplateXSignaturesChanged', documentTemplateXSignatures: newSignatures });
    } else {
      const newSignatures = localState.documentTemplate.documentTemplateXSignatures.filter(p => p.signatureId !== signatureId);
      dispatch({ type: 'OnDocumentTemplateXSignaturesChanged', documentTemplateXSignatures: newSignatures });
    }
  };

  const handleImplicitClick = (event, signatureId) => {
    const newSignatures = localState.documentTemplate.documentTemplateXSignatures.map(item => {
      return item.signatureId === signatureId ? { ...item, implicit: event } : { ...item, implicit: false };
    });

    dispatch({ type: 'OnDocumentTemplateXSignaturesChanged', documentTemplateXSignatures: newSignatures });
  };

  const handleSaveDocumentTemplateXSignatures = () => {
    saveDocumentTemplateXSignatures({
      variables: {
        input: {
          documentTemplateXSignatures: localState.documentTemplate.documentTemplateXSignatures,
          documentTemplateId: localState.documentTemplate.documentTemplateId,
          needsSigning: localState.documentTemplate.needsSigning,
          requiredDocTypeId: localState.documentTemplate.requiredDocTypeId
        }
      }
    });
  };

  const handleSaveSignatureBookmarks = () => {
    if (!validateSignatureBookmark(localState.documentTemplate.signatureBookmarks)) return;

    setSaveBookmarksDisabled(true);
    saveSignatureBookmarks({
      variables: {
        input: {
          documentTemplateId: localState.documentTemplate.documentTemplateId,
          signatureBookmarks: localState.documentTemplate.signatureBookmarks
        }
      }
    });
    resetDocumentTemplateSignatureBookmarkDirtyInfo();
    resetDirtyInfoDocumentTemplateSignatureBookmark();
  };

  const handleBookmarkPropertyChanged = index => (propName, propValue) => {
    setDocumentTemplateSignatureBookmarkDirtyInfo(`${index}.${propName}`);
    dispatch({ type: 'OnSignatureBookmarkChanged', propName, propValue, index });
  };

  const handleLookupPropertyChanged = index => event => {
    dispatch({
      type: 'OnSignatureBookmarkActorChanged',
      signatureActors: signatureActorsData?.signatureActors.filter(x => event.includes(x.actorId)),
      index
    });
  };

  const handleAddNewBookmark = () => {
    let templateId = localState.documentTemplate.documentTemplateId;
    dispatch({
      type: 'OnNewBookmarkAdded',
      templateId
    });
  };

  const handleBookmarkDeleted = index => () => {
    if (window.confirm(t('DocumentTemplate.DeleteBookmark'))) {
      dispatch({ type: 'OnBookmarkDeleted', index });
    }
  };

  const handleOptionBookmarkDeleted = index => () => {
    if (window.confirm(t('DocumentTemplate.DeleteBookmark'))) {
      dispatch({ type: 'OnDocumentTemplateXOptionBookmarkDeleted', index });
    }
  };

  const handleOptionBookmarkPropertyChanged = index => (propName, propValue) => {
    setDocumentTemplateXoptionsBookmarkDirtyInfo(`${index}.${propName}`);
    dispatch({ type: 'OnDocumentTemplateXOptionBookmarkChanged', propName, propValue, index });
  };

  const handleLookupOptionBookmarkPropertyChanged = index => (propName, event) => {
    dispatch({
      type: 'OnDocumentTemplateXOptionBookmarkChanged',
      propName,
      propValue: event,
      index
    });
  };

  const handleAddNewOptionBookmark = () => {
    let templateId = localState.documentTemplate.documentTemplateId;
    dispatch({
      type: 'OnNewDocumentTemplateXOptionBookmarkAdded',
      templateId
    });
  };

  const handleSaveDocumentTemplateXoptionBookmarks = () => {
    if (!validateDocumentTemplateXoptionsBookmark(localState.documentTemplate.documentTemplateXOptionBookmarks)) return;
    setSaveDocumentTemplateXOptionsBookmarkDisabled(true);
    const input = {
      documentTemplateId: localState.documentTemplate.documentTemplateId,
      documentTemplateXOptionBookmarks: localState.documentTemplate.documentTemplateXOptionBookmarks.map(item => {
        return {
          id: item.id,
          bookmarkName: item.bookmarkName,
          dataSource: item.dataSource,
          templateId: item.templateId
        };
      })
    };

    saveDocumentTemplateXoptionBookmarks({
      variables: {
        input: input
      }
    });
    resetDocumentTemplateXoptionsBookmarkDirtyInfo();
    resetDirtyInfoDocumentTemplateXoptionsBookmark();
  };

  const handleDocumentTemplateNeedsSigningChanged = value => {
    if (value === false) {
      dispatch({ type: 'onDocumentTemplateSignaturePropertyChanged', propertyName: 'requiredDocTypeId', value: null });
      dispatch({ type: 'OnDocumentTemplateXSignaturesChanged', documentTemplateXSignatures: [] });
    }
    dispatch({ type: 'onDocumentTemplateSignaturePropertyChanged', propertyName: 'needsSigning', value });
  };

  const handleHeaderPropertyChanged = (propertyName, value) => {
    dispatch({ type: 'onDocumentTemplateSignaturePropertyChanged', propertyName, value });
  };

  useSubscription(DOCUMENT_TEMPLATE_SIGNATURES_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('DocumentTemplate.SavingSucceeded'), 'success');
      dispatch({
        type: 'OnDocumentTemplateXSignaturesSaved'
      });

      if (data.documentTemplateXSignaturesSaved.documentTemplateXSignatures.length > 0)
        client.cache.evict({
          id: `DocumentTemplateItem:{"documentTemplateId":${data.documentTemplateXSignaturesSaved.documentTemplateXSignatures[0].documentTemplateId}}`
        });
    }
  });

  useSubscription(SIGNATURE_BOOKMARKS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      setSaveBookmarksDisabled(false);
      let signatureBookmarks = data.signatureBookmarksUpdated.signatureBookmarks;
      addToast(t('DocumentTemplate.BookmarksUpdated'), 'success');
      dispatch({
        type: 'OnSignatureBookmarksUpdated',
        signatureBookmarks
      });

      if (data.signatureBookmarksUpdated.signatureBookmarks.length > 0)
        client.cache.evict({
          id: `DocumentTemplateItem:{"documentTemplateId":${data.signatureBookmarksUpdated.signatureBookmarks[0].templateId}}`
        });
    }
  });

  useSubscription(DOCUMENT_TEMPLATE_X_OPTION_BOOKMARKS_UPDATED, {
    onData: ({ data: { data }, client }) => {
      setSaveDocumentTemplateXOptionsBookmarkDisabled(false);
      let documentTemplateXOptionBookmarks = data.documentTemplateXOptionBookmarksUpdated.documentTemplateXOptionBookmarks;
      addToast(t('DocumentTemplate.BookmarksUpdated'), 'success');
      dispatch({
        type: 'OnDocumentTemplateXOptionBookmarksUpdated',
        documentTemplateXOptionBookmarks
      });

      if (data.documentTemplateXOptionBookmarksUpdated.documentTemplateXOptionBookmarks.length > 0)
        client.cache.evict({
          id: `DocumentTemplateItem:{"documentTemplateId":${data.documentTemplateXOptionBookmarksUpdated.documentTemplateXOptionBookmarks[0].templateId}}`
        });
    }
  });

  return (
    <>
      {loadingSignatureTypes || loadingRequiredDocTypes ? (
        <Card>
          <FakeText lines={5} />
        </Card>
      ) : (
        <DocumentTemplateSignatureCard
          documentTemplate={localState.documentTemplate}
          signatureTypes={signatureTypesData?.signatureTypes || []}
          requiredDocTypes={requiredDocTypesData?.requiredDocTypes || []}
          onSelectedClick={handleSelectedClick}
          onImplicitClick={handleImplicitClick}
          onHeaderPropertyChanged={handleHeaderPropertyChanged}
          onSaveDocumentTemplateXSignatures={handleSaveDocumentTemplateXSignatures}
          onDocumentTemplateNeedsSigningChanged={handleDocumentTemplateNeedsSigningChanged}
          documentTemplateXSignaturesValidation={documentTemplateXSignaturesValidation}
          signatureActors={signatureActorsData?.signatureActors || []}
          loadingSignatureActors={loadingSignatureActors}
          onSaveSignatureBookmarks={handleSaveSignatureBookmarks}
          saveBookmarksDisabled={saveBookmarksDisabled}
          signatureBookmarkValidation={signatureBookmarkValidation}
          onBookmarkPropertyChanged={handleBookmarkPropertyChanged}
          onLookupPropertyChanged={handleLookupPropertyChanged}
          onAddNewBookmark={handleAddNewBookmark}
          onBookmarkDeleted={handleBookmarkDeleted}
          optionSources={optionSourcesData?.optionSources || []}
          loadingOptionSources={loadingOptionSources}
          onSaveDocumentTemplateXoptionBookmarks={handleSaveDocumentTemplateXoptionBookmarks}
          documentTemplateXoptionsBookmarkValidation={documentTemplateXoptionsBookmarkValidation}
          onAddNewOptionBookmark={handleAddNewOptionBookmark}
          onOptionBookmarkPropertyChanged={handleOptionBookmarkPropertyChanged}
          onLookupOptionBookmarkPropertyChanged={handleLookupOptionBookmarkPropertyChanged}
          saveDocumentTemplateXoptionsBookmarkDisabled={saveDocumentTemplateXoptionsBookmarkDisabled}
          onOptionBookmarkDeleted={handleOptionBookmarkDeleted}
        />
      )}
    </>
  );
};

DocumentTemplateSignatureContainer.propTypes = {
  data: PropTypes.object
};

export default DocumentTemplateSignatureContainer;
